import { ArrowLeft, RotateCcw, Trophy } from 'lucide-react';
import { Link } from 'react-router';
import { useLearner } from '@/app/useAppState';
import { Avatar } from '@/components/ui/Avatar';
import { SampleBadge, STAGE, SubjectBadge } from '@/components/ui/Badges';
import { ButtonLink } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loading } from '@/components/ui/Loading';
import { Mascot } from '@/components/ui/Mascot';
import { WhatsNewBanner } from '@/features/news/WhatsNew';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { ScreenHeader } from '@/components/ui/ScreenHeader';
import { DAILY_MISSION } from '@/config/learning';
import { curriculum } from '@/content/curriculum';
import { missionDoneOn } from '@/features/games/dailyMission';
import {
  recommendationPath,
  useMastery,
  useRecommendations,
  useSessions,
} from '@/features/learning/hooks';
import { isSecure } from '@/features/mastery/model';
import { useI18n } from '@/i18n/context';
import { isolateAuto } from '@/lib/bidi';
import { cn } from '@/lib/cn';

export function HomeScreen() {
  const learner = useLearner();
  const { t, l } = useI18n();
  const recommendations = useRecommendations(learner);
  const mastery = useMastery(learner.id);
  const sessions = useSessions(learner.id);
  if (!recommendations || !mastery || !sessions) return <Loading label={t('app.loading')} />;

  const top = recommendations[0];
  const topObjective = top ? curriculum.objective(top.objectiveId) : undefined;
  const topSubject = topObjective ? curriculum.subject(topObjective.subjectId) : undefined;
  const dueCount = recommendations.filter((r) => r.kind === 'review').length;
  const missionDone = missionDoneOn(sessions, new Date());
  const subjects = curriculum.subjectsFor(learner.levelId);
  const objectives = curriculum.objectivesForLevel(learner.levelId, learner.variantId);
  const flowers = objectives.filter(
    (o) => mastery.get(o.id)?.status === 'provisionalMastery',
  ).length;
  const level = curriculum.level(learner.levelId);

  return (
    <div className="flex flex-col gap-8">
      <section className="flex items-center gap-4">
        <Avatar avatar={learner.avatar} size="lg" />
        <ScreenHeader
          className="flex-1"
          title={
            learner.nickname
              ? t('home.greeting', { name: isolateAuto(learner.nickname) })
              : t('home.greetingAnonymous')
          }
          subtitle={
            <span className="flex flex-wrap items-center gap-2">
              {t('home.subtitle')}
              {level && (
                <span className="rounded-full bg-surface px-2.5 py-0.5 text-sm font-bold shadow-sm">
                  {l(level.name)}
                </span>
              )}
            </span>
          }
        />
        <Mascot className="hidden size-24 sm:block" />
      </section>

      <WhatsNewBanner />

      <div className="grid gap-4 md:grid-cols-2">
        {top && topObjective && topSubject && (
          <Card
            className={cn(
              `subject-${topSubject.color}`,
              'flex flex-col gap-4 border-s-8 border-[var(--subject)]',
            )}
          >
            <p className="text-sm font-extrabold tracking-wide text-[var(--subject-ink)] uppercase">
              {t('home.continueTitle')}
            </p>
            <div className="flex items-center gap-3">
              <SubjectBadge subject={topSubject} />
              <div className="min-w-0">
                <h2 className="text-xl font-extrabold">{l(topObjective.title)}</h2>
                <p className="text-sm text-ink-muted">
                  {t(`recommend.kind.${top.kind}`)} · {l(topSubject.name)}
                </p>
              </div>
            </div>
            <ButtonLink
              to={recommendationPath(top)}
              size="lg"
              icon={<ArrowLeft aria-hidden className="size-5 ltr:-scale-x-100" />}
              className="flex-row-reverse self-start"
            >
              {t('home.continueCta')}
            </ButtonLink>
          </Card>
        )}

        <Card className="flex flex-col gap-3 bg-sun-100">
          <div className="flex items-center gap-2">
            <span aria-hidden className="emoji text-3xl">
              🌟
            </span>
            <h2 className="text-xl font-extrabold">{t('home.missionTitle')}</h2>
          </div>
          <p className="text-ink">
            {missionDone
              ? t('home.missionDone')
              : t('home.missionBody', { count: DAILY_MISSION.questions })}
          </p>
          <ButtonLink
            to="/learn/play/daily-mission"
            variant={missionDone ? 'secondary' : 'sun'}
            size="lg"
            className="self-start"
          >
            {missionDone ? t('home.missionAgain') : t('home.missionCta')}
          </ButtonLink>
        </Card>
      </div>

      <section aria-labelledby="subjects-title" className="flex flex-col gap-4">
        <h2 id="subjects-title" className="text-xl font-extrabold">
          {t('home.subjects')}
        </h2>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {subjects.map((subject) => {
            const list = curriculum.objectivesFor(subject.id, learner.levelId, learner.variantId);
            const secure = list.filter((o) => isSecure(mastery.get(o.id)?.status)).length;
            return (
              <li key={subject.id}>
                <Link
                  to={`/learn/subjects/${subject.id}`}
                  className={cn(
                    `subject-${subject.color}`,
                    'flex items-center gap-4 rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)] transition-transform hover:-translate-y-0.5',
                  )}
                >
                  <SubjectBadge subject={subject} />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <span className="font-extrabold">{l(subject.name)}</span>
                    <ProgressBar
                      value={secure}
                      max={list.length}
                      tone="subject"
                      label={`${l(subject.name)}: ${secure}/${list.length}`}
                    />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-labelledby="garden-title" className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="garden-title" className="text-xl font-extrabold">
            {t('home.garden')}
          </h2>
          <span className="text-sm font-bold text-brand-700">
            {t('home.flowers', { count: flowers })}
          </span>
        </div>
        <p className="text-sm text-ink-muted">{t('home.gardenHint')}</p>
        <ul className="flex flex-wrap gap-2 rounded-[var(--radius-card)] bg-brand-50 p-4">
          {objectives.map((objective) => {
            const status = mastery.get(objective.id)?.status ?? 'notStarted';
            return (
              <li
                key={objective.id}
                className="emoji text-3xl"
                title={`${l(objective.title)} — ${t(`mastery.status.${status}`)}`}
              >
                <span aria-hidden className={status === 'notStarted' ? 'opacity-40' : ''}>
                  {STAGE[status].emoji}
                </span>
                <span className="sr-only">
                  {l(objective.title)}: {t(`mastery.status.${status}`)}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/learn/review"
          className="flex items-center gap-3 rounded-2xl bg-surface p-4 font-bold shadow-[var(--shadow-card)]"
        >
          <RotateCcw aria-hidden className="size-6 text-sky-700" />
          <span>{t('home.reviewDue', { count: dueCount })}</span>
        </Link>
        <Link
          to="/learn/rewards"
          className="flex items-center gap-3 rounded-2xl bg-surface p-4 font-bold shadow-[var(--shadow-card)]"
        >
          <Trophy aria-hidden className="size-6 text-sun-700" />
          <span>{t('home.rewardsLink')}</span>
        </Link>
      </div>

      <p className="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
        <SampleBadge />
        {t('app.sampleContentNotice')}
      </p>
    </div>
  );
}
